import { AgentChatMessage } from './AgentChat.types';
import { AgentRun } from './AgentChat.types';

type PendingMessage = { runId: string; message: AgentChatMessage };

/**
 * Coalesces high-frequency stream IPC updates into one React commit per frame.
 */
export const createAgentStreamBatcher = (
  apply: (updater: (runs: AgentRun[]) => AgentRun[]) => void,
) => {
  const pending = new Map<string, PendingMessage>();
  let frame: number | null = null;

  const mergeMessage = (runs: AgentRun[], { runId, message }: PendingMessage) =>
    runs.map((item) => {
      if (item.id !== runId) return item;
      const index = item.messages.findIndex((entry) => entry.id === message.id);
      if (index === -1) {
        return { ...item, messages: [...item.messages, message] };
      }
      const messages = [...item.messages];
      messages[index] = message;
      return { ...item, messages };
    });

  const flush = () => {
    frame = null;
    if (pending.size === 0) return;
    const batch = [...pending.values()];
    pending.clear();
    apply((current) =>
      batch.reduce((runs, entry) => mergeMessage(runs, entry), current),
    );
  };

  const queue = (runId: string, message: AgentChatMessage) => {
    pending.set(`${runId}:${message.id}`, { runId, message });
    if (frame === null) {
      frame = requestAnimationFrame(flush);
    }
  };

  const flushNow = () => {
    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
    flush();
  };

  const dispose = () => {
    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
    pending.clear();
  };

  return { queue, flushNow, dispose };
};
